import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { listFactors, challengeAndVerify } from "@/lib/mfa";
import { supabase } from "@/integrations/supabase/client";

type Props = {
  onVerified: () => void;
  onCancel?: () => void;
};

export default function MfaChallenge({ onVerified, onCancel }: Props) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { verified } = await listFactors();
      setFactorId(verified[0]?.id ?? null);
    })();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setError(null);
    setLoading(true);
    try {
      await challengeAndVerify(factorId, code);
      onVerified();
    } catch (err: any) {
      setError(err?.message ?? "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    onCancel?.();
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="w-full max-w-[420px] bg-card border border-border rounded-xl p-8 shadow-sm">
        <h1 className="text-2xl font-semibold mb-2">Two-factor verification</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Enter the 6-digit code from your authenticator app.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code">Authentication code</Label>
            <Input
              id="mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
            />
          </div>
          {error && (
            <div role="alert" className="text-destructive text-sm bg-destructive/10 p-3 rounded-md">
              {error}
            </div>
          )}
          <Button type="submit" className="w-full" disabled={loading || code.length < 6 || !factorId}>
            {loading ? "Verifying…" : "Verify"}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={signOut}>
            Cancel and sign out
          </Button>
          {!factorId && (
            <p className="text-xs text-muted-foreground text-center">
              No verified authenticator found. Please contact your administrator to reset MFA.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
